import { Controller } from "@hotwired/stimulus"
import Rails from "@rails/ujs";
import debounce from "lodash/debounce";

export default class extends Controller {
  static targets = [
    "exceptGroupSelector",
    "selectedGroupSelector",
    "exceptSelectedTags",
    "selectedSelectedTags",
    "exceptSearchInput",
    "selectedSearchInput",
    "exceptOptions",
    "selectedOptions",
    "exceptLoading",
    "selectedLoading",
    "exceptNoResults",
    "selectedNoResults",
    "exceptList",
    "selectedList"
  ]

  static values = {
    searchGroupsUrl: String
  }

  connect() {
    this.selectedGroupIds = [];
    this.selectedGroups = []; // Store group objects with id and name
    this.isOpen = false;
    this.searchTerm = "";
    this.loading = false;
    this.highlightedIndex = -1; // Track currently highlighted option for keyboard navigation
    this.lastInScopeGroupTypes = [];

    // Initialize with existing selected groups from the page first
    this.initializeExistingSelections();

    // Initialize visibility based on current radio button selection after loading existing selections
    this.initializeVisibility();

    // Initialize radio button states based on group type selections
    this.updateRadioButtonStates();

    // Add form submission handler
    this.setupFormSubmission();

    // Add click outside handler
    this.setupClickOutsideHandler();
  }

  setupFormSubmission() {
    const form = this.element.closest('form');
    if (form) {
      form.addEventListener('submit', () => {
        this.updateHiddenFields();
      });

      // Also update hidden fields when any form input changes
      form.addEventListener('change', () => {
        this.updateHiddenFields();
      });
    }
  }

  setupClickOutsideHandler() {
    this.handleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.handleClickOutside);
  }

  handleClickOutside(event) {
    // Check if the click is outside any multi-select dropdown
    const clickedElement = event.target;
    const isInsideMultiSelect = clickedElement.closest('.google-workspace-multi-select');

    if (!isInsideMultiSelect) {
      this.closeDropdown();
    }
  }

  disconnect() {
    // Clean up event listeners
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside);
    }
  }

  initializeExistingSelections() {
    // Clear any existing selections first
    this.selectedGroupIds = [];
    this.selectedGroups = [];

    // Get existing selected groups from both except and selected containers
    const exceptTags = this.hasExceptSelectedTagsTarget ? this.exceptSelectedTagsTarget.querySelectorAll('.google-workspace-multi-select__tag') : [];
    const selectedTags = this.hasSelectedSelectedTagsTarget ? this.selectedSelectedTagsTarget.querySelectorAll('.google-workspace-multi-select__tag') : [];

    // Process except tags
    exceptTags.forEach(tag => {
      const groupId = tag.querySelector('.google-workspace-multi-select__tag-remove')?.dataset.groupId;
      const groupName = tag.querySelector('.google-workspace-multi-select__tag-text')?.textContent;

      if (groupId && groupName) {
        this.selectedGroupIds.push(groupId);
        this.selectedGroups.push({ external_id: groupId, name: groupName });
      }
    });

    // Process selected tags
    selectedTags.forEach(tag => {
      const groupId = tag.querySelector('.google-workspace-multi-select__tag-remove')?.dataset.groupId;
      const groupName = tag.querySelector('.google-workspace-multi-select__tag-text')?.textContent;

      if (groupId && groupName) {
        this.selectedGroupIds.push(groupId);
        this.selectedGroups.push({ external_id: groupId, name: groupName });
      }
    });

    // Initialize hidden fields with current values
    this.updateHiddenFields();
  }

  initializeVisibility() {
    const selectedRadio = document.querySelector('input[name="import_mode"]:checked');
    if (selectedRadio) {
      this.toggleImportMode({ target: selectedRadio });
    }
  }

  toggleImportMode(event) {
    const importMode = event.target.value;

    // Hide all group selectors first
    if (this.hasExceptGroupSelectorTarget) {
      this.exceptGroupSelectorTarget.style.display = 'none';
    }
    if (this.hasSelectedGroupSelectorTarget) {
      this.selectedGroupSelectorTarget.style.display = 'none';
    }

    // Only clear selections when user manually switches between modes, not during initialization
    if (event.isTrusted && this.selectedGroupIds.length > 0) {
      this.clearSelectedGroups();
    }

    if (importMode === 'all') {
      // Clear selections when switching to "all" mode
      if (event.isTrusted) {
        this.clearSelectedGroups();
      }
    } else if (importMode === 'except') {
      if (this.hasExceptGroupSelectorTarget) {
        this.exceptGroupSelectorTarget.style.display = 'block';
      }
    } else if (importMode === 'selected') {
      if (this.hasSelectedGroupSelectorTarget) {
        this.selectedGroupSelectorTarget.style.display = 'block';
      }
    }
  }

  toggleGroupTypes(event) {
    // Handle group type changes - enable/disable radio buttons based on selections
    this.updateRadioButtonStates();
  }

  updateRadioButtonStates() {
    // Get all checked group type checkboxes
    const checkedBoxes = document.querySelectorAll('input[name="integrations_google_workspace_integration[in_scope_group_types][]"]:checked');
    const hasSelections = checkedBoxes.length > 0;

    // Enable/disable all radio buttons based on group type selections
    const radioButtons = document.querySelectorAll('input[name="import_mode"]');
    radioButtons.forEach(radio => {
      radio.disabled = !hasSelections;
    });

    // Enable/disable search inputs in group selectors
    if (this.hasExceptSearchInputTarget) {
      this.exceptSearchInputTarget.disabled = !hasSelections;
    }
    if (this.hasSelectedSearchInputTarget) {
      this.selectedSearchInputTarget.disabled = !hasSelections;
    }

    // Disable/enable the exclude identities checkbox
    const excludeIdentitiesCheckbox = document.querySelector('input[name="integrations_google_workspace_integration[exclude_identities_in_selected_groups]"]');
    if (excludeIdentitiesCheckbox) {
      excludeIdentitiesCheckbox.disabled = !hasSelections;
      // Also update the label cursor styling
      const excludeLabel = excludeIdentitiesCheckbox.closest('.exclude-identities-checkbox')?.querySelector('.form-check-label');
      if (excludeLabel) {
        if (!hasSelections) {
          excludeLabel.style.cursor = 'not-allowed';
          excludeLabel.style.color = '#9ca3af';
        } else {
          excludeLabel.style.cursor = '';
          excludeLabel.style.color = '';
        }
      }
    }

    // Also disable/enable the multi-select input containers
    const multiSelectInputs = document.querySelectorAll('.google-workspace-multi-select__input');
    multiSelectInputs.forEach(input => {
      if (!hasSelections) {
        input.style.pointerEvents = 'none';
        input.style.opacity = '0.5';
        input.style.cursor = 'not-allowed';
      } else {
        input.style.pointerEvents = '';
        input.style.opacity = '';
        input.style.cursor = '';
      }
    });
  }


  async searchGroups() {
    if (this.loading) {
      return;
    }

    // Check if group types are selected - if not, don't search
    const checkedBoxes = document.querySelectorAll('input[name="integrations_google_workspace_integration[in_scope_group_types][]"]:checked');
    const hasSelections = checkedBoxes.length > 0;

    if (!hasSelections) {
      return;
    }

    // Get the selected in-scope group types
    const selectedTypes = Array.from(checkedBoxes).map(box => box.value);

    const bustCache = selectedTypes.join(",") !== this.lastInScopeGroupTypes.join(",");
    if (bustCache) {
      this.lastInScopeGroupTypes = selectedTypes;
    }

    this.loading = true;
    this.showLoading();

    // Determine which dropdown is active
    const activeDropdown = this.getActiveDropdown();
    if (!activeDropdown) {
      this.loading = false;
      return;
    }

    const searchParams = new URLSearchParams({
      search_term: this.searchTerm || "",
      page: 1,
      bust_cache: bustCache,
      target_id: activeDropdown.id
    });

    // Add in-scope group types to the search params
    selectedTypes.forEach(type => {
      searchParams.append('in_scope_group_types[]', type);
    });

    // Add selected group IDs so server can mark them as selected
    if (this.selectedGroupIds.length > 0) {
      searchParams.append('selected_external_ids', this.selectedGroupIds.join(','));
    }

    try {
      const url = `${this.searchGroupsUrlValue}?${searchParams}`;
      const res = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
      });
      const html = await res.text();

      // Use Turbo to render the stream
      Turbo.renderStreamMessage(html);

      // Show the dropdown after the stream is processed
      setTimeout(() => {
        this.showDropdown(activeDropdown);
        // Reset highlighted index when new results are loaded
        this.highlightedIndex = -1;
        this.updateHighlightedOption();
        // Update selection state to ensure all selected groups are visually marked
        this.updateGroupSelection();
      }, 50);

      this.hideLoading();
    } catch (error) {
      console.error("Error fetching groups:", error);
      this.hideLoading();
      this.showNoResults();
    }

    this.loading = false;
  }


  getActiveDropdown() {
    // Find which dropdown should be active based on the current radio button selection
    const importMode = document.querySelector('input[name="import_mode"]:checked')?.value;

    if (importMode === 'except') {
      return document.getElementById('except-group-options-list');
    } else if (importMode === 'selected') {
      return document.getElementById('selected-group-options-list');
    }

    // Default to except dropdown
    return document.getElementById('except-group-options-list');
  }

  // This method will be called by Turbo Stream responses
  renderGroupOptions() {
    // This will be handled by Turbo Streams
  }

  toggleGroup(event) {
    event.preventDefault();
    event.stopImmediatePropagation(); // Prevent event bubbling and immediate propagation

    // Get the group data from the clicked element or its parent
    const target = event.target.closest('[data-group-id]');
    if (!target) return;

    const groupId = target.dataset.groupId;
    const groupName = target.dataset.groupName;

    const index = this.selectedGroupIds.indexOf(groupId);
    if (index > -1) {
      // Remove group
      this.selectedGroupIds.splice(index, 1);
      this.selectedGroups.splice(index, 1);
    } else {
      // Add group
      this.selectedGroupIds.push(groupId);
      this.selectedGroups.push({ external_id: groupId, name: groupName });

      // Clear the search input text but keep dropdown open with results
      this.clearSearchInput();

      // Reset highlighted index after selection
      this.highlightedIndex = -1;
      this.updateHighlightedOption();
    }

    this.updateSelectedTags();
    this.updateGroupSelection(groupId, groupName, index === -1);
    this.updateHiddenFields();
  }

  clearSearchInput() {
    // Clear the active search input based on which dropdown is currently visible
    const importMode = document.querySelector('input[name="import_mode"]:checked')?.value;

    if (importMode === 'except' && this.hasExceptSearchInputTarget) {
      this.exceptSearchInputTarget.value = '';
      this.searchTerm = '';
    } else if (importMode === 'selected' && this.hasSelectedSearchInputTarget) {
      this.selectedSearchInputTarget.value = '';
      this.searchTerm = '';
    }

    // Re-run search with empty term to show all results again
    this.searchGroups();
  }

  updateGroupSelection(groupId, groupName, isSelected) {
    // Update the visual state of all options to match current selection
    document.querySelectorAll('.google-workspace-multi-select__option').forEach(option => {
      const optionGroupId = option.dataset.groupId;
      if (this.selectedGroupIds.includes(optionGroupId)) {
        option.classList.add('google-workspace-multi-select__option--selected');
      } else {
        option.classList.remove('google-workspace-multi-select__option--selected');
      }
    });
  }

  removeTag(event) {
    event.stopPropagation();
    const groupId = event.currentTarget.dataset.groupId;
    const index = this.selectedGroupIds.indexOf(groupId);

    if (index > -1) {
      this.selectedGroupIds.splice(index, 1);
      this.selectedGroups.splice(index, 1);
      this.updateSelectedTags();
      this.updateGroupSelection();
      this.updateHiddenFields();
    }
  }

  updateSelectedTags() {
    const tagsHtml = this.selectedGroups.map(group => `
      <span class="google-workspace-multi-select__tag">
        <span class="google-workspace-multi-select__tag-text">${group.name}</span>
        <span class="google-workspace-multi-select__tag-remove"
              data-action="click->google-workspace-integration#removeTag"
              data-group-id="${group.external_id}">×</span>
      </span>
    `).join('');

    // Only update the currently visible target based on radio button selection
    const importMode = document.querySelector('input[name="import_mode"]:checked')?.value;

    if (importMode === 'except' && this.hasExceptSelectedTagsTarget) {
      this.exceptSelectedTagsTarget.innerHTML = tagsHtml;
    } else if (importMode === 'selected' && this.hasSelectedSelectedTagsTarget) {
      this.selectedSelectedTagsTarget.innerHTML = tagsHtml;
    }
  }

  clearSelectedGroups() {
    this.selectedGroupIds = [];
    this.selectedGroups = [];
    this.updateSelectedTags();
    this.updateGroupSelection();
  }

  // Method to update hidden fields before form submission
  updateHiddenFields() {
    const selectedField = document.getElementById('selected_google_group_external_ids');
    const excludedField = document.getElementById('excluded_google_group_external_ids');

    if (selectedField && excludedField) {
      const selectedGroups = this.getSelectedGroups();
      const importMode = document.querySelector('input[name="import_mode"]:checked')?.value;

      if (importMode === 'selected') {
        selectedField.value = selectedGroups.join(',');
        excludedField.value = '';
      } else if (importMode === 'except') {
        selectedField.value = '';
        excludedField.value = selectedGroups.join(',');
      } else {
        selectedField.value = '';
        excludedField.value = '';
      }
    }
  }

  getSelectedGroups() {
    // Return the selectedGroupIds array directly
    return this.selectedGroupIds;
  }

  search = debounce((event) => {
    this.searchTerm = event.target.value;

    // Reset highlighted index when searching
    this.highlightedIndex = -1;

    // Always search, even with empty search term
    this.searchGroups();
  }, 300);

  // Handle focus events immediately (no debounce)
  handleFocus(event) {
    // Check if group types are selected - if not, don't allow focus/search
    const checkedBoxes = document.querySelectorAll('input[name="integrations_google_workspace_integration[in_scope_group_types][]"]:checked');
    const hasSelections = checkedBoxes.length > 0;

    if (!hasSelections) {
      event.target.blur(); // Remove focus if disabled
      return;
    }

    this.searchTerm = event.target.value;

    // Reset highlighted index when focusing
    this.highlightedIndex = -1;
    this.updateHighlightedOption();

    // Search immediately on focus
    this.searchGroups();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeDropdown();
      this.highlightedIndex = -1;
      return;
    }

    // Get the active dropdown
    const activeDropdown = this.getActiveDropdown();
    if (!activeDropdown) return;

    const options = activeDropdown.querySelectorAll('.google-workspace-multi-select__option');
    if (options.length === 0) return;

    // Handle arrow key navigation
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlightedIndex = this.highlightedIndex < options.length - 1
        ? this.highlightedIndex + 1
        : this.highlightedIndex;
      this.updateHighlightedOption();
      this.scrollToHighlightedOption();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightedIndex = this.highlightedIndex > 0
        ? this.highlightedIndex - 1
        : 0;
      this.updateHighlightedOption();
      this.scrollToHighlightedOption();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.highlightedIndex >= 0 && this.highlightedIndex < options.length) {
        const highlightedOption = options[this.highlightedIndex];
        // Trigger the click event on the highlighted option
        highlightedOption.dispatchEvent(new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true
        }));
      }
    }
  }

  updateHighlightedOption() {
    const activeDropdown = this.getActiveDropdown();
    if (!activeDropdown) return;

    const options = activeDropdown.querySelectorAll('.google-workspace-multi-select__option');

    // Remove highlight from all options
    options.forEach(option => {
      option.classList.remove('google-workspace-multi-select__option--highlighted');
    });

    // Add highlight to the selected option
    if (this.highlightedIndex >= 0 && this.highlightedIndex < options.length) {
      options[this.highlightedIndex].classList.add('google-workspace-multi-select__option--highlighted');
    }
  }

  scrollToHighlightedOption() {
    const activeDropdown = this.getActiveDropdown();
    if (!activeDropdown) return;

    const options = activeDropdown.querySelectorAll('.google-workspace-multi-select__option');
    if (this.highlightedIndex >= 0 && this.highlightedIndex < options.length) {
      const highlightedOption = options[this.highlightedIndex];
      highlightedOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  toggleDropdown(event) {
    // Check if group types are selected - if not, don't allow opening
    const checkedBoxes = document.querySelectorAll('input[name="integrations_google_workspace_integration[in_scope_group_types][]"]:checked');
    const hasSelections = checkedBoxes.length > 0;

    if (!hasSelections) {
      return;
    }

    const input = event.currentTarget;
    const options = input.nextElementSibling;

    if (options && options.classList.contains('google-workspace-multi-select__options')) {
      if (options.style.display === 'block') {
        options.style.display = 'none';
      } else {
        // Close other dropdowns first
        this.closeDropdown();
        options.style.display = 'block';

        // Load groups
        this.searchGroups();
      }
    }
  }

  showDropdown(targetElement) {
    // Find the options container by looking for the element with the target ID
    const targetId = targetElement.id;

    // Find the options container that contains this list
    let optionsContainer;
    if (targetId === 'except-group-options-list') {
      optionsContainer = this.exceptOptionsTarget;
    } else if (targetId === 'selected-group-options-list') {
      optionsContainer = this.selectedOptionsTarget;
    }

    if (optionsContainer) {
      optionsContainer.style.display = 'block';
    }
  }

  closeDropdown() {
    // Close any open dropdowns
    const openDropdowns = document.querySelectorAll('.google-workspace-multi-select__options[style*="block"]');
    openDropdowns.forEach(dropdown => {
      dropdown.style.display = 'none';
    });

    // Reset highlighted index when closing dropdown
    this.highlightedIndex = -1;
    this.updateHighlightedOption();
  }

  toggle() {
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    this.isOpen = true;
    this.optionsTarget.style.display = 'block';
    this.searchInputTarget.focus();

    if (this.availableGroups.length === 0) {
      this.searchGroups();
    } else {
      this.renderGroupOptions();
    }
  }

  showLoading() {
    if (this.hasExceptLoadingTarget) {
      this.exceptLoadingTarget.style.display = 'block';
    }
    if (this.hasSelectedLoadingTarget) {
      this.selectedLoadingTarget.style.display = 'block';
    }
    this.hideNoResults();
  }

  hideLoading() {
    if (this.hasExceptLoadingTarget) {
      this.exceptLoadingTarget.style.display = 'none';
    }
    if (this.hasSelectedLoadingTarget) {
      this.selectedLoadingTarget.style.display = 'none';
    }
  }

  showNoResults() {
    if (this.hasExceptNoResultsTarget) {
      this.exceptNoResultsTarget.style.display = 'block';
    }
    if (this.hasSelectedNoResultsTarget) {
      this.selectedNoResultsTarget.style.display = 'block';
    }
  }

  hideNoResults() {
    if (this.hasExceptNoResultsTarget) {
      this.exceptNoResultsTarget.style.display = 'none';
    }
    if (this.hasSelectedNoResultsTarget) {
      this.selectedNoResultsTarget.style.display = 'none';
    }
  }
}
